import { Button, useBoolean, useDisclosure } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { useCreateCreditAccountMutation } from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { omit } from "lodash";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import type React from "react";
import { type FC, useEffect, useState } from "react";
import { getPayloadBudgetCategoryIds, useData } from "./Common";
import { FormCreditAccountModal } from "./FromCreditAccountModal";
import {
  FormCreditAccountCtx,
  StoreCreditAccountForm,
} from "./StoreCreditAccountForm";

export const ModalCreateCreditAccount: FC<
  React.PropsWithChildren<{ clientId?: number; brandId?: number }>
> = observer(function ModalCreateCreditAccount({ clientId, brandId }) {
  const [store] = useState(() => {
    return StoreCreditAccountForm.create();
  });
  const [loading, { toggle }] = useBoolean(false);

  const router = useRouter();
  const modal = useDisclosure();

  const { data } = useData(store);

  useEffect(() => {
    if (data) {
      store.fillDefaultCreditAccountForm();
    }
  }, [modal.isOpen]);

  const [createCreditAccount] = useCreateCreditAccountMutation();

  const onSubmit = async () => {
    const payload = await store.form.validate();

    if (payload === null) return;

    toggle();
    const { data } = await createCreditAccount({
      variables: {
        input: {
          ...omit(payload, "budget_category_ids"),
          our_work_budget_category_ids: getPayloadBudgetCategoryIds(
            payload,
            store,
            store.workOptions,
          ),
          expense_budget_category_ids: getPayloadBudgetCategoryIds(
            payload,
            store,
            store.expenseOptions,
          ),
        },
      },
    });

    if (data?.createBrandCreditAccount.id) {
      router.push({
        pathname: "/credit-accounts/detail",
        query: { id: data?.createBrandCreditAccount.id },
      });
    }
    toggle();
  };

  const onOpenModal = () => {
    store.form.form.reset();
    modal.onOpen();
  };

  return (
    <FormCreditAccountCtx.Provider value={store}>
      <Button leftIcon={<Icon name="plus" />} onClick={onOpenModal}>
        <Trans>Add Credit Account</Trans>
      </Button>

      <FormCreditAccountModal
        clientId={clientId}
        brandId={brandId}
        isEditForm={false}
        title={t`Create Credit Account`}
        {...modal}
        primaryAction={{
          title: t`Create Credit Account`,
          action: onSubmit,
          loading: loading,
        }}
      />
    </FormCreditAccountCtx.Provider>
  );
});
